import clsx from "clsx";
import { useSyncExternalStore } from "react";
import { useMedia } from "./media.js";

/*
 * The right block (docs/web.md, "Layout"): side panels — the assistant's
 * chat, help — sit in a slot inside the centre, right of the page. From
 * `DOCK_WIDE` up a panel there narrows the page and its content reflows
 * (sessions refit: their panes observe their size); below it a panel lies
 * over the page instead, and on phones it is a sheet. Panels outside the
 * router (help) reach the slot too, so it is a module-level store, not a
 * React context.
 *
 * A panel is a card floating in the main area (owner's request of
 * 2026-10-22: the chat was glued to the window's edges): the radius, border
 * and a soft shadow of the panel's cards, and the page's own margin above,
 * right of and below it — `py-6` is the page's top margin and `gap-6` the
 * gap between its sections.
 */

/** From this width a side panel narrows the centre instead of covering it. */
export const DOCK_WIDE = "(min-width: 1280px)";

/** The look of a side panel: a card like the page's (`Card`: `rounded-xl border`), lifted a little. */
export const DOCK_PANEL = "overflow-hidden rounded-xl border border-line bg-surface shadow-md";

/**
 * Where a panel lies over the page below `DOCK_WIDE`: the same margins as in
 * the slot, under the top bar (`h-14` and the safe area).
 */
export const DOCK_OVERLAY =
  "fixed bottom-6 right-6 top-[calc(5rem+env(safe-area-inset-top))] z-40 max-w-[calc(100vw-3rem)] shadow-xl";

let slot: HTMLElement | null = null;
const listeners = new Set<() => void>();

/** A stable ref callback: React gives the element on mount and null on unmount. */
function setSlot(element: HTMLDivElement | null): void {
  if (slot === element) return;
  slot = element;
  for (const listener of listeners) listener();
}

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

/**
 * The slot itself; the shell renders it right of the page, inside the
 * centre. Its padding is the panels' margin: above, on the right and below;
 * on the left too beside a page without margins of its own (`bleed`: the
 * session workspace, the dashboard's canvas), which would touch the card.
 */
export function DockSlot({ bleed = false }: { bleed?: boolean }) {
  return (
    <div
      ref={setSlot}
      data-dock=""
      className={clsx("flex min-h-0 shrink-0 gap-6 py-6 pr-6 empty:hidden", bleed && "pl-6")}
    />
  );
}

/**
 * Where a side panel goes now: the slot (portal into it) on wide screens,
 * or null — then the panel lies over the page as before.
 */
export function useDockTarget(): HTMLElement | null {
  const wide = useMedia(DOCK_WIDE);
  const element = useSyncExternalStore(
    subscribe,
    () => slot,
    () => null,
  );
  return wide ? element : null;
}
